import { getTgUserId } from "utils/tgWebApp"
import type {
  DiagnosticsSnapshot,
  FpsSample,
  ImgErrorSample,
  LongTaskSample,
  MemorySample,
  RouteChangeSample,
  SnapshotMeta,
  VisibilitySample
} from "./types"

const CAP = {
  memory: 60,
  fps: 60,
  longtasks: 100,
  imgErrors: 50,
  visibility: 30,
  routes: 30
} as const

function pushCapped<T>(buf: T[], item: T, cap: number): void {
  buf.push(item)
  if (buf.length > cap) buf.shift()
}

interface PerformanceWithMemory extends Performance {
  memory?: {
    usedJSHeapSize: number
    totalJSHeapSize: number
    jsHeapSizeLimit: number
  }
}

interface NavigatorWithDeviceMemory extends Navigator {
  deviceMemory?: number
}

class Recorder {
  private memory: MemorySample[] = []
  private fps: FpsSample[] = []
  private longtasks: LongTaskSample[] = []
  private imgErrors: ImgErrorSample[] = []
  private visibilityEvents: VisibilitySample[] = []
  private routeChanges: RouteChangeSample[] = []

  private longTaskObserver: PerformanceObserver | null = null
  private started = false

  // FPS accounting
  private fpsFrameCount = 0
  private fpsDroppedCount = 0
  private fpsLastSampleMs = 0
  private fpsLastFrameMs = 0

  start(): void {
    if (this.started) return
    this.started = true

    this.startMemorySampler()
    this.startFpsSampler()
    this.startLongTaskObserver()
    this.startImgErrorListener()
    this.startVisibilityListener()
  }

  /** Called from <RouteWatcher/> inside DiagReportButton when react-router location changes. */
  pushRoute(from: string, to: string): void {
    pushCapped(this.routeChanges, { t: Date.now(), from, to }, CAP.routes)
  }

  snapshot(): DiagnosticsSnapshot {
    const readIsUnder = (): boolean => {
      try {
        return localStorage.getItem("isUnder") === "true"
      } catch {
        return false
      }
    }

    const meta: SnapshotMeta = {
      telegramId: getTgUserId() ?? null,
      userAgent: navigator.userAgent,
      deviceMemory:
        (navigator as NavigatorWithDeviceMemory).deviceMemory ?? null,
      hardwareConcurrency: navigator.hardwareConcurrency,
      viewport: { w: window.innerWidth, h: window.innerHeight },
      isUnder: readIsUnder(),
      route: window.location.pathname
    }

    return {
      capturedAt: Date.now(),
      meta,
      memory: [...this.memory],
      fps: [...this.fps],
      longtasks: [...this.longtasks],
      imgErrors: [...this.imgErrors],
      visibilityEvents: [...this.visibilityEvents],
      routeChanges: [...this.routeChanges]
    }
  }

  // ---------- private samplers ----------

  private startMemorySampler(): void {
    const perf = performance as PerformanceWithMemory

    const sample = () => {
      const m = perf.memory
      pushCapped(
        this.memory,
        {
          t: Date.now(),
          used: m?.usedJSHeapSize ?? null,
          total: m?.totalJSHeapSize ?? null,
          limit: m?.jsHeapSizeLimit ?? null
        },
        CAP.memory
      )
    }

    sample()
    window.setInterval(sample, 1000)
  }

  private startFpsSampler(): void {
    this.fpsLastSampleMs = performance.now()
    this.fpsLastFrameMs = this.fpsLastSampleMs

    const tick = (now: number) => {
      const delta = now - this.fpsLastFrameMs
      this.fpsLastFrameMs = now

      this.fpsFrameCount++
      // 16.7ms is target frame; >32ms ≈ skipped frame
      if (delta > 32) this.fpsDroppedCount++

      if (now - this.fpsLastSampleMs >= 1000) {
        const elapsed = now - this.fpsLastSampleMs
        const avg = Math.round((this.fpsFrameCount * 1000) / elapsed)
        pushCapped(
          this.fps,
          { t: Date.now(), avg, dropped: this.fpsDroppedCount },
          CAP.fps
        )
        this.fpsFrameCount = 0
        this.fpsDroppedCount = 0
        this.fpsLastSampleMs = now
      }

      window.requestAnimationFrame(tick)
    }

    window.requestAnimationFrame(tick)
  }

  private startLongTaskObserver(): void {
    if (typeof PerformanceObserver === "undefined") return

    try {
      this.longTaskObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          pushCapped(
            this.longtasks,
            {
              t: Date.now(),
              duration: Math.round(entry.duration),
              name: entry.name
            },
            CAP.longtasks
          )
        }
      })
      this.longTaskObserver.observe({ entryTypes: ["longtask"] })
    } catch {
      // longtask not supported — skip silently
    }
  }

  private startImgErrorListener(): void {
    document.addEventListener(
      "error",
      (event) => {
        const target = event.target
        if (!(target instanceof HTMLImageElement)) return
        pushCapped(
          this.imgErrors,
          {
            t: Date.now(),
            src: target.currentSrc || target.src || "",
            route: window.location.pathname
          },
          CAP.imgErrors
        )
      },
      true // capture phase to catch <img> errors anywhere in the tree
    )
  }

  private startVisibilityListener(): void {
    document.addEventListener("visibilitychange", () => {
      pushCapped(
        this.visibilityEvents,
        {
          t: Date.now(),
          state: document.visibilityState === "visible" ? "visible" : "hidden"
        },
        CAP.visibility
      )
    })
  }
}

export const diagnosticsRecorder = new Recorder()
